import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import Button from "../../../../../../../core/ui/Button";
import {
  ArrowLeftIcons,
  CloseIcon,
  PlusIcon,
  RightIcon,
} from "../../../../../../../core/global/Icons";
import CompanyInfoStep from "./CompanyInfoStep";
import IncaseOfOthersStep from "./IncaseOfOthersStep";
import EngagementsStep from "./EngagementsStep";
import CustomerIdentificationStep from "./CustomerIdentificationStep";
import { KYC_STEPS } from "../../../../../../../core/utility/constants";
import { ToolContext } from "../../../../../../../core/context/ToolContext";
import StatusBadge from "../StatusBadge";
import {
  validateKYCCompanyInfoStep,
  validateEngagementDetailsStep,
} from "../../../../../../../core/validator/Validator";

const INITIAL_FORM = {
  fullName: "",
  legalStatus: "",
  permanentAddress: "",
  businessAddress: "",
  secRegistration: "",
  purposeOfTransaction: [],
  nationalityCitizenship: "",
  occupationProfession: "",
  establishmentEmployer: "",
  currentAddress: "",
  individualContactNumber: "",
  locationOfActivity: "",
  sourceFundsApplicability: "Applicable",
  individualSourceFunds: ["Business", "Employment", "Investments", "Remittances", "Pensions"],
  individualSourceOther: "",
  individualPEP: "No",
  cityCountryIncorporation: "",
  principalBusiness: "",
  dateOfIncorporation: "",
  actingOnBehalf: "No",
  beneficialOwnerName: "",
  beneficialOwnerID: "",
  domicile: "",
  nationality: "",
  isPEP: "No",
  regulatoryStatus: "",
  nameOfRegulatory: "",
  nameOfStockExchange: "",
  subsidiaryBranches: "",
  groupCompany: "",
  businessCountries: "",
  contactPerson: "",
  complianceOfficer: "",
  telephone: "",
  mobile: "",
  emailWebsite: "",
  engagements: [
    {
      id: 1,
      engagementType: "",
      reportingPeriod: "",
      endDate: "",
      coverageFrom: "",
      coverageTo: "",
    },
  ],
  documents: {},
};

export default function KYCFormPage() {
  const history = useHistory();
  const { lead_id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [version, setVersion] = useState("V1");
  const [versions, setVersions] = useState(["V1"]);
  const [versionCreatedAt, setVersionCreatedAt] = useState(true);

  const { tool_state } = useContext(ToolContext);
  const { __SESSION } = tool_state;

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleEngagementChange = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      engagements: prev.engagements.map((eng) =>
        eng.id === id ? { ...eng, [field]: value } : eng,
      ),
    }));
    setErrors((prev) => {
      if (!prev.engagements) return prev;
      const idx = form.engagements.findIndex((eng) => eng.id === id);
      if (idx === -1) return prev;
      const newEngErrors = [...prev.engagements];
      if (newEngErrors[idx]) {
        newEngErrors[idx] = { ...newEngErrors[idx] };
        delete newEngErrors[idx][field];
      }
      return { ...prev, engagements: newEngErrors };
    });
  };

  const handleAddEngagement = () => {
    const newId =
      form.engagements.length > 0
        ? Math.max(...form.engagements.map((e) => e.id)) + 1
        : 1;
    setForm((prev) => ({
      ...prev,
      engagements: [
        ...prev.engagements,
        {
          id: newId,
          engagementType: "",
          reportingPeriod: "",
          endDate: "",
          coverageFrom: "",
          coverageTo: "",
        },
      ],
    }));
  };

  const handleRemoveEngagement = (id) => {
    if (form.engagements.length > 1) {
      setForm((prev) => ({
        ...prev,
        engagements: prev.engagements.filter((eng) => eng.id !== id),
      }));
    }
  };

  const validateStep = () => {
    let stepErrors = {};
    if (activeStep === 0) {
      stepErrors = validateKYCCompanyInfoStep(form);
    }
    if (activeStep === 2) {
      const result = validateEngagementDetailsStep(form);
      const hasErrors = result.engagements?.some(
        (e) => Object.keys(e).length > 0,
      );
      if (hasErrors) stepErrors.engagements = result.engagements;
    }
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleBack = () => {
    if (activeStep === 0) {
      history.push(
        lead_id
          ? `/bd/lead-profile/${lead_id}?view=client-acceptance`
          : "/bd/leads",
      );
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
      setErrors({});
    }
  };

  const handleSave = () => {
    history.push(
      lead_id
        ? `/bd/lead-profile/${lead_id}?view=client-acceptance`
        : "/bd/leads",
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <CompanyInfoStep
            form={form}
            handleChange={handleChange}
            errors={errors}
          />
        );
      case 1:
        return (
          <IncaseOfOthersStep
            form={form}
            handleChange={handleChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <EngagementsStep
            form={form}
            handleEngagementChange={handleEngagementChange}
            handleAddEngagement={handleAddEngagement}
            handleRemoveEngagement={handleRemoveEngagement}
            errors={errors}
          />
        );
      case 3:
        return (
          <CustomerIdentificationStep
            form={form}
            handleChange={handleChange}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fbfcfb",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          py: 2,
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <IconButton
          type="button"
          onClick={handleBack}
          sx={{
            color: "#666",
            width: 32,
            height: 32,
            "&:hover": { bgcolor: "#f0f0f0" },
          }}
        >
          <ArrowLeftIcons sx={{ fontSize: 36 }} />
        </IconButton>
        <Typography sx={{ fontWeight: 600, fontSize: 22, color: "#292C2F" }}>
          KYC Form
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Avatar
            variant="circle"
            src={JSON.parse(__SESSION.data.pinfo[0].profile).profile}
            sx={{ width: 32, height: 32 }}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <Box
          sx={{
            width: "321px",
            bgcolor: "#FFFFFF",
            borderRight: "1px solid #e0e0e0",
            overflow: "auto",
            px: 3,
            pt: "24px",
            pb: "24px",
            flexShrink: 0,
            boxShadow:
              "0px 2px 4px rgba(0,0,0,0.08), 0px 0px 6px rgba(0,0,0,0.02)",
          }}
        >
          <Box
            sx={{
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#292C2F",
                mb: 1,
              }}
            >
              Version:
            </Typography>
            <Select
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              fullWidth
              size="small"
              sx={{
                borderRadius: "8px",
                mb: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
            >
              {versions.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="dark"
              onClick={() => {
                const newVersion = `V${versions.length + 1}`;
                setVersions((prev) => [...prev, newVersion]);
                setVersion(newVersion);
              }}
              sx={{ width: "100%", height: 44 }}
            >
              Add new version
            </Button>
          </Box>
          {KYC_STEPS.map((label, index) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 2,
                px: 3,
                mx: -3,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor:
                    index < activeStep
                      ? "#003F2E"
                      : index === activeStep
                        ? "#003F2E"
                        : "#c8ccd0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {index < activeStep ? (
                  <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                    <path
                      d="M1 5.5L5 9.5L13 1.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : index === activeStep ? (
                  <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                    <path
                      d="M1 5.5L5 9.5L13 1.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                    <path
                      d="M1 5.5L5 9.5L13 1.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#292C2F",
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {versionCreatedAt && activeStep !== 2 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
              <Typography sx={{ fontSize: 14, color: "#292C2F" }}>
                {version.toLowerCase()} - {versionCreatedAt}
              </Typography>
              <StatusBadge statusKey="submitted" />
            </Box>
          )}

          {activeStep === 2 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              {versionCreatedAt ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: 14, color: "#292C2F" }}>
                    {version.toLowerCase()} - {versionCreatedAt}
                  </Typography>
                  <StatusBadge statusKey="submitted" />
                </Box>
              ) : (
                <Typography
                  sx={{ fontSize: "22px", fontWeight: 600, color: "#292C2F" }}
                >
                  {KYC_STEPS[activeStep]}
                </Typography>
              )}

              <Button
                variant="primary"
                onClick={handleAddEngagement}
                sx={{ height: 52, fontWeight: 600, width: "157px" }}
              >
                Add Engagment
              </Button>
            </Box>
          )}

          {activeStep === 2 || activeStep === 1 || activeStep === 0 ? (
            renderStepContent()
          ) : (
            <Box
              sx={
                activeStep === 3
                  ? {}
                  : {
                      bgcolor: "#fff",
                      borderRadius: "12px",
                      padding: 3,
                      boxShadow:
                        "0px 2px 4px rgba(0,0,0,0.08), 0px 0px 6px rgba(0,0,0,0.02)",
                    }
              }
            >
              {renderStepContent()}
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderTop: "1px solid #e0e0e0",
          px: 5,
          py: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          onClick={handleBack}
          variant="tertiary"
          sx={{ height: 44 }}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>

        {activeStep === KYC_STEPS.length - 1 ? (
          <Button variant="dark" onClick={handleSave} sx={{ height: 44 }}>
            Submit
          </Button>
        ) : activeStep === 0 ? (
          <Button variant="dark" onClick={handleNext} sx={{ height: 44 }}>
            Next
          </Button>
        ) : (
          <Button variant="dark" onClick={handleNext} sx={{ height: 44 }}>
            Save
          </Button>
        )}
        <Divider orientation="vertical" sx={{ mx: 1, height: 24, alignSelf: "center" }} />
        <Button variant="approved" sx={{ height: 44, fontWeight: 600 }} startIcon={<RightIcon/>}>
          Approved
        </Button>
        <Button variant="notApproved" sx={{ height: 44, fontWeight: 600 }} startIcon={<CloseIcon/>}>
          Not Approved
        </Button>
      </Box>
    </Box>
  );
}
